import React, { useState, useEffect, useCallback } from 'react';
import { tripsAPI } from '../utils/api';
import './TripList.css';

const TripList = ({ onSelectTrip, currentUserId }) => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMyTrips, setShowMyTrips] = useState(false);

  const loadTrips = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      if (showMyTrips && currentUserId) {
        response = await tripsAPI.getMyTrips();
      } else {
        response = await tripsAPI.getPublicTrips();
      }
      
      setTrips(response.data);
    } catch (err) {
      console.error('Error loading trips:', err);
      setError('Failed loading trips');
    } finally {
      setLoading(false);
    }
  }, [showMyTrips, currentUserId]);

  useEffect(() => {
    loadTrips();
  }, [loadTrips]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays === 1 ? '1 day' : `${diffDays} days`;
  };

  if (loading) {
    return (
      <div className="trip-list loading">
        <div className="loading-spinner">Loading trips...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trip-list error">
        <div className="error-message">{error}</div>
        <button onClick={loadTrips} className="retry-button">Retry</button>
      </div>
    );
  }

  return (
    <div className="trip-list">
      <div className="trip-list-header">
        <h2>Trips</h2>
        <div className="trip-filter">
          <button 
            className={`filter-button ${!showMyTrips ? 'active' : ''}`}
            onClick={() => setShowMyTrips(false)}
          >
            Public Trips
          </button>
          <button 
            className={`filter-button ${showMyTrips ? 'active' : ''}`}
            onClick={() => setShowMyTrips(true)}
            disabled={!currentUserId}
          >
            My Trips
          </button>
        </div>
      </div>

      {trips.length === 0 ? (
        <div className="empty-state">
          <p>No trips found</p>
          {showMyTrips && <p>Create your first trip to get started!</p>}
        </div>
      ) : (
        <div className="trips-grid">
          {trips.map(trip => (
            <div 
              key={trip.id} 
              className="trip-card"
              onClick={() => onSelectTrip(trip)}
            >
              {trip.coverImage && (
                <div className="trip-cover">
                  <img src={trip.coverImage} alt={trip.title} />
                </div>
              )}
              
              <div className="trip-info">
                <h3 className="trip-title">{trip.title}</h3>
                
                {trip.description && (
                  <p className="trip-description">
                    {trip.description.length > 100 
                      ? trip.description.substring(0, 100) + '...' 
                      : trip.description}
                  </p>
                )}
                
                <div className="trip-meta">
                  <span className="trip-dates">
                    {formatDate(trip.startDate)} - {formatDate(trip.endDate)}
                  </span>
                  <span className="trip-duration">
                    {getDuration(trip.startDate, trip.endDate)}
                  </span>
                  <span className="trip-post-count">
                    {trip.postCount} {trip.postCount === 1 ? 'post' : 'posts'}
                  </span>
                </div>

                {trip.user && (
                  <div className="trip-author">
                    {trip.user.avatar && (
                      <img 
                        src={trip.user.avatar} 
                        alt={trip.user.fullName}
                        className="author-avatar"
                      />
                    )}
                    <span className="author-name">{trip.user.fullName}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TripList;
